import {
	AuditedRVRequest,
	MCInfo,
	PossibleErrorResponse,
	PropertiesResponse,
	RVFeeItem,
	RVMode,
	RVPropertyType,
	RVRequest,
	RVResponse,
	RVSearchQuery,
	RVSearchType,
	RVSubmitResponse,
	SearchOption,
} from "./entities";
import appConfig from "./config";
import { LocaleUtil, StringUtil } from "@eres/core";
import Strings from "@my-dld/assets/strings";
import { httpGet, httpPost, httpPostRaw } from "@eres/rest";
import { toParams } from "@eres/ejari-api/api";

export const DefaultSearchOption: SearchOption = {
	requestorTypeId: RVMode.OWN,
	userTypeId: 0,
	propertyTypeId: RVPropertyType.LAND,
	managementCompanyId: 0,
	searchTypeId: RVSearchType.LandNumber,
	startIndex: 0,
	maxRows: 100,
	isEjariUser: 0,
	participantId: undefined,
	propertyNumber: undefined,
	buildingName: undefined,
	dewaPremise: undefined,
	areaId: undefined,
	dmNo: undefined,
	dmSubNo: undefined,
	landNo: undefined,
	landSubNo: undefined,
	certificateNumber: undefined,
	certificateYear: undefined,
	contractNumber: undefined,
	searchedPropertyTypeId: undefined,
	emiratesId: undefined,
	propertyId: undefined,
	isFreehold: undefined,
};

export interface RVLookup {
	CodeCategory: string;
	ErrorDescAr: string;
	ErrorDescEn: string;
	ErrorEnum: string;
	ErrorEnumCode: string;
	Identity: number;
}

export interface RVLookupX {
	NameEn: string;
	NameAr: string;
	Identity: number;
}

export interface RVDocumentLookup extends RVLookupX {
	IsMandatory: 0 | 1;
	MaxPages: number;
	MaxSizeKb: number;
}

export interface RVRequestSearchResult extends PossibleErrorResponse {
	TotalRecords: number;
	IsSuccess: boolean;
	Data: AuditedRVRequest[];
}

export function searchProperties( opt: SearchOption ): Promise<RVResponse<PropertiesResponse>> {
	const o = {
		...DefaultSearchOption,
		...opt,
	};
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/Properties/SearchAllProperties?` + toParams( o ) );
}

export function loadLookups( id: string, parm1: string = "", param2: string = "" ): Promise<RVResponse<RVLookup[]>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/Lookups/GetData?name=${ id }&param1=${ parm1 }&param2=${ param2 }` );
}

export function loadLookupsX( id: string, parm1: string = "", param2: string = "" ): Promise<RVResponse<RVLookupX[]>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/Lookups/GetData?name=${ id }&param1=${ parm1 }&param2=${ param2 }` );
}

export function uploadDocument( file: File, type: string ): Promise<RVResponse<{ TemporaryRequestId: number, Url: string }>> {
	const data = new FormData();
	data.append( "document", file, file.name );
	return httpPostRaw( appConfig.rentalValuationAPI().endPoint() +
		"api/RentalEvaluation/UploadFile?docId=" + type, data );
}

export function submitRVRequest( request: RVRequest ): Promise<RVResponse<RVSubmitResponse>> {
	return httpPost( appConfig.rentalValuationAPI().endPoint() + `api/RentalEvaluation/SubmitRequest`, request );
}

export function searchRVRequest( request: RVSearchQuery ): Promise<RVResponse<RVRequestSearchResult>> {
	return httpPost( appConfig.rentalValuationAPI().endPoint() + `api/RentalEvaluation/Search`, request );
}

export function getRVRequestDetails( requestNumber: string ): Promise<RVResponse<AuditedRVRequest>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/RentalEvaluation/GetByNumber?requestNumber=${ requestNumber }` );
}

export type ReportType = "GetRequestReport" | "GetPaymentReceiptReport" | "GetEvaluationCertificateReport";

export function downloadRVReport( requestNumber: string, reportType: ReportType ): Promise<RVResponse<string>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/RentalEvaluation/${ reportType }?requestNumber=${ encodeURIComponent( requestNumber ) }` );
}

export function feeDetails( requestNumber: string ): Promise<RVResponse<RVFeeItem[]>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/RentalEvaluation/GetPaymentDetailsByRequestId?requestId=${ encodeURIComponent( requestNumber ) }` );
}

export function getMCInfo( mcId: number ): Promise<RVResponse<MCInfo>> {
	return httpGet( appConfig.rentalValuationAPI().endPoint() + `api/Companies/GetCompanyByParticipantId?managementCompanyId=${ encodeURIComponent( mcId ) }` );
}


export function validateResponse<T extends PossibleErrorResponse>( response: RVResponse<T> ): Promise<T> {
	if ( response ) {
		if ( response.Code === "0" ) {
			const r = response.Response;
			if ( r ) {
				let errors: number[] = [];
				if ( r.ValidationErrors && r.ValidationErrors.length > 0 )
					errors.push( ...r.ValidationErrors );
				if ( r.Error && r.Error.length > 0 )
					errors.push( ...r.Error );
				r.ValidationErrors = errors;
			}
			if ( r && r.ValidationErrors && r.ValidationErrors.length > 0 ) {
				const errorCodes = r.ValidationErrors.map( value => "" + value );
				return loadLookups( "RVValidationMessages" ).then( value => {
					const errors = value.Response;
					const e: RVLookup[] = [];
					if ( errors && errors.length > 0 ) {
						errors.forEach( ( lkp, index ) => {
							const ix = lkp.ErrorEnumCode;
							if ( errorCodes.indexOf( ix ) > -1 ) {
								e.push( lkp );
							}
						} );
					}
					if ( e.length > 0 ) {
						const error = e.map( value1 => {
							return {
								en: value1.ErrorDescEn,
								ar: value1.ErrorDescAr,
							};
						} ).map( value1 => LocaleUtil.getLocalValue( value1 ) )
						.join( " " );
						const ex = {
							message: error,
							type: "ValidationError",
							codes: e,
						};
						return Promise.reject( ex );
					} else {
						const error = errorCodes.join( " " );
						const ex = {
							message: error,
							type: "ValidationError",
						};
						return Promise.reject( ex );
					}
				} );
			}
			return Promise.resolve( response.Response );
		} else {
			let reason = response.Message;
			if ( reason )
				return Promise.reject( reason );
			else {
				let errors = response.Errors;
				if ( errors && errors.length > 0 ) {
					reason = "";
					errors.reduce( ( previousValue, currentValue ) => {
						if ( currentValue.Message ) {
							reason = reason + "\n" + LocaleUtil.getLocalValue( {
								en: currentValue.Message.En,
								ar: currentValue.Message.Ar
							} );
						}
						return reason;
					}, reason );
					if ( !StringUtil.isEmptyStringOrNull( response.ExceptionId ) )
						reason = reason + "\n" + response.ExceptionId;
					return Promise.reject( reason );
				}
			}
			return Promise.reject( Strings.system_error );
		}
	} else {
		return Promise.reject( "Invalid Response" );
	}
}

export function validateResponseX<T>( response: RVResponse<T> ): Promise<T> {
	if ( response ) {
		if ( response.Code === "0" ) {
			return Promise.resolve( response.Response );
		} else {
			return Promise.reject( response.Message );
		}
	} else {
		return Promise.reject( "Invalid Response" );
	}
}

const reasonCache: RVLookupX[] = [];

export function convertReasonIdToLookup( id: number ): Promise<RVLookupX> {
	let lookup = reasonCache.find( x => x.Identity === id );
	if ( lookup )
		return Promise.resolve( lookup );
	return loadLookupsX( "RVReasons" ).then( validateResponseX ).then( value => {
		reasonCache.splice( 0, reasonCache.length );
		reasonCache.push( ...value );
		lookup = reasonCache.find( x => x.Identity === id );
		if ( lookup )
			return Promise.resolve( lookup );
		return Promise.reject( "Invalid ID" );
	} ).catch( ex => {
		return Promise.reject( "Invalid ID" );
	} );
}
